import type { ContentVersionBlob, PostOption } from '@opallabs/api/v3'
import {
  canonicalIANATimezoneIdentifier,
  timeZoneName,
  utcOffsetForZoneAsString,
} from '@opallabs/concepts'

export type VersionDetail = { label: string; value: string }

/** "standard_post" → "Standard post"; used when the blob has no display name. */
const humanize = (value: string): string => {
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * "PDT (-7)", not "America/Los_Angeles" — the zone as a reader recognizes it.
 * Falls back to the stored identifier for a zone we hold no data for, and keeps
 * it alongside the offset for zones Intl names by their offset alone (those come
 * back with no short name, and "GMT-3 (-3)" says the same thing twice).
 */
const timezoneLabel = (date: Date, timezone: string): string => {
  const canonical = canonicalIANATimezoneIdentifier(timezone)
  if (!canonical) return timezone

  const { short } = timeZoneName(date, canonical)
  const offset = utcOffsetForZoneAsString(date, canonical)
  const name = short ?? timezone
  return offset ? `${name} (${offset})` : name
}

const formatScheduled = (
  scheduledAt: string,
  timezone: string | undefined
): string => {
  const date = new Date(scheduledAt)
  const formatted = date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  // The helpers below throw on an invalid date, and a blob can carry anything.
  if (!timezone || Number.isNaN(date.getTime())) return formatted
  return `${formatted} · ${timezoneLabel(date, timezone)}`
}

const optionDisplayName = (option: PostOption): string | null => {
  const record = option.option_record as
    | { option?: { display_name?: string | null; input_type?: string | null } }
    | undefined
  return record?.option?.display_name ?? null
}

const optionInputType = (option: PostOption): string | null => {
  const record = option.option_record as
    | { option?: { input_type?: string | null } }
    | undefined
  return record?.option?.input_type ?? null
}

const optionValueText = (option: PostOption): string | null => {
  if (option.option_value?.display_name) return option.option_value.display_name
  const raw = option.value
  if (raw === true) return 'Yes'
  if (raw === false) return 'No'
  if (typeof raw === 'string' && raw.trim().length > 0) return raw
  return null
}

/**
 * The version attributes that don't appear in the rendered preview itself:
 * scheduling, status, labels, and any post options carrying a value. The name is
 * omitted — it heads the single-version view. Asset options are omitted too —
 * their content already renders in the preview.
 */
export const getVersionDetails = (
  blob: ContentVersionBlob
): VersionDetail[] => {
  const { post } = blob
  const details: VersionDetail[] = []

  const contentTypeDisplayName = post.service_post_type?.display_name
  if (contentTypeDisplayName) {
    details.push({ label: 'Content type', value: contentTypeDisplayName })
  } else if (typeof post.content_type === 'string' && post.content_type) {
    details.push({ label: 'Content type', value: humanize(post.content_type) })
  }
  if (typeof post.status === 'string' && post.status) {
    details.push({ label: 'Status', value: humanize(post.status) })
  }
  if (typeof post.scheduled_at === 'string' && post.scheduled_at) {
    details.push({
      label: 'Scheduled',
      value: formatScheduled(
        post.scheduled_at,
        typeof post.scheduled_at_timezone === 'string'
          ? post.scheduled_at_timezone
          : undefined
      ),
    })
  }
  const labelNames = (post.labels ?? [])
    .map((label) => label?.name)
    .filter((name): name is string => Boolean(name))
  if (labelNames.length > 0) {
    details.push({ label: 'Labels', value: labelNames.join(', ') })
  } else {
    // Blobs captured before label names were enriched only carry ids.
    const labelIds = Array.isArray(post.label_ids) ? post.label_ids : []
    if (labelIds.length > 0) {
      details.push({
        label: 'Labels',
        value: `${labelIds.length} ${labelIds.length === 1 ? 'label' : 'labels'}`,
      })
    }
  }

  // Sectioned content (a carousel, say) repeats its options once per section, so
  // the table would otherwise show several rows all labelled "Title". Number the
  // repeats in the order the sections appear; a name used once keeps its label.
  const optionRows: VersionDetail[] = []
  for (const option of post.post_options) {
    if (optionInputType(option)?.includes('asset')) continue
    const name = optionDisplayName(option)
    const value = optionValueText(option)
    if (name && value) optionRows.push({ label: name, value })
  }

  const rowsPerLabel = new Map<string, number>()
  for (const row of optionRows) {
    rowsPerLabel.set(row.label, (rowsPerLabel.get(row.label) ?? 0) + 1)
  }

  const seenPerLabel = new Map<string, number>()
  for (const row of optionRows) {
    if ((rowsPerLabel.get(row.label) ?? 0) < 2) {
      details.push(row)
      continue
    }
    const position = (seenPerLabel.get(row.label) ?? 0) + 1
    seenPerLabel.set(row.label, position)
    details.push({ label: `${row.label} ${position}`, value: row.value })
  }

  return details
}
